import React, { useMemo } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { AdminReportSalesPoint } from '../../../types/api'

type AdminSalesChartProps = {
  data: AdminReportSalesPoint[]
  isLoading?: boolean
}

const AdminSalesChart: React.FC<AdminSalesChartProps> = ({ data, isLoading = false }) => {
  const chartData = useMemo(() => {
    return [...data]
      .filter((point) => point?.date)
      .map((point) => ({
        ...point,
        date: point.date,
        parsedDate: new Date(point.date)
      }))
      .filter((point) => !Number.isNaN(point.parsedDate.getTime()))
      .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime())
      .map((point) => ({
        ...point,
        displayDate: point.parsedDate
      }))
  }, [data])

  const dateFormatter = useMemo(() => {
    if (chartData.length === 0) {
      return () => ''
    }

    const first = chartData[0].displayDate
    const last = chartData[chartData.length - 1].displayDate
    const spanInDays = (last.getTime() - first.getTime()) / (1000 * 60 * 60 * 24)

    if (spanInDays > 365) {
      return (date: Date) =>
        new Intl.DateTimeFormat('pt-BR', {
          month: 'short',
          year: 'numeric'
        }).format(date)
    }

    if (spanInDays > 90) {
      return (date: Date) =>
        new Intl.DateTimeFormat('pt-BR', {
          month: 'short'
        }).format(date)
    }

    if (spanInDays > 30) {
      return (date: Date) =>
        new Intl.DateTimeFormat('pt-BR', {
          day: 'numeric',
          month: 'short'
        }).format(date)
    }

    return (date: Date) =>
      new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit'
      }).format(date)
  }, [chartData])

  const currencyFormatter = useMemo(() => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0
    })
  }, [])

  const tooltipFormatter = (value: number, name: string) => {
    if (name === 'revenue') {
      return [currencyFormatter.format(value), 'Receita']
    }

    if (name === 'orders') {
      return [`${value.toLocaleString('pt-BR')} pedidos`, 'Pedidos']
    }

    return [value, name]
  }

  const tooltipLabelFormatter = (label: string) => {
    const date = new Date(label)
    if (Number.isNaN(date.getTime())) return label
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(date)
  }

  const legendFormatter = (value: string | number) => {
    if (value === 'revenue') {
      return 'Receita'
    }
    if (value === 'orders') {
      return 'Pedidos'
    }
    return String(value)
  }

  if (chartData.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg bg-gray-50">
        <p className="text-sm text-gray-500">
          {isLoading ? 'Carregando dados do gráfico...' : 'Nenhum dado disponível para o período selecionado.'}
        </p>
      </div>
    )
  }

  return (
    <div
      role="img"
      aria-label="Gráfico de desempenho de vendas"
      className="relative h-64 w-full"
    >
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-sm">
          <span className="text-sm font-medium text-gray-600">Atualizando...</span>
        </div>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 20, right: 24, bottom: 8, left: 0 }}>
          <defs>
            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="ordersGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(value: string) => {
              const parsed = new Date(value)
              return Number.isNaN(parsed.getTime()) ? value : dateFormatter(parsed)
            }}
            stroke="#6b7280"
            fontSize={12}
          />
          <YAxis
            yAxisId="left"
            dataKey="revenue"
            tickFormatter={(value: number) => currencyFormatter.format(value)}
            stroke="#2563eb"
            fontSize={12}
            width={100}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            dataKey="orders"
            tickFormatter={(value: number) => `${value}`}
            stroke="#22c55e"
            fontSize={12}
            width={60}
          />
          <Tooltip
            formatter={tooltipFormatter as never}
            labelFormatter={tooltipLabelFormatter}
            contentStyle={{
              borderRadius: '0.75rem',
              borderColor: '#e2e8f0',
              boxShadow: '0 10px 25px -15px rgba(15, 23, 42, 0.35)',
              padding: '0.75rem 1rem'
            }}
          />
          <Legend formatter={legendFormatter} />
          <Area
            type="monotone"
            dataKey="revenue"
            name="revenue"
            stroke="#2563eb"
            strokeWidth={2}
            fill="url(#revenueGradient)"
            yAxisId="left"
            dot={{ r: 3, strokeWidth: 1, stroke: '#2563eb', fill: '#fff' }}
            activeDot={{ r: 5 }}
          />
          <Area
            type="monotone"
            dataKey="orders"
            name="orders"
            stroke="#22c55e"
            strokeWidth={2}
            fill="url(#ordersGradient)"
            yAxisId="right"
            dot={{ r: 3, strokeWidth: 1, stroke: '#22c55e', fill: '#fff' }}
            activeDot={{ r: 5 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default AdminSalesChart

